import { ref, onMounted, onBeforeUnmount } from '#imports';

const cancelIdle = (id) =>
  typeof cancelIdleCallback === 'undefined'
    ? clearTimeout(id)
    : cancelIdleCallback(id);

/**
 * Returns a `ref<boolean>` that flips to `true` the first time any of these
 * happen: the target element scrolls near the viewport, the user interacts with
 * the page, or the main thread goes idle.
 *
 * Use it to keep heavy client-only widgets (Swiper carousels, third-party
 * embeds) from initialising during hydration and the first-input window, which
 * is what INP measures.
 *
 * @param {import('vue').Ref<HTMLElement | undefined>} target
 * @param {{ rootMargin?: string, idleTimeout?: number }} [options]
 */
export function useDeferredMount(
  target,
  { rootMargin = '400px', idleTimeout = 4000 } = {}
) {
  const ready = ref(false);
  const controller = new AbortController();

  let observer;
  let idleId;

  const scheduleIdle = (callback) =>
    typeof requestIdleCallback === 'undefined'
      ? setTimeout(callback, 2000)
      : requestIdleCallback(callback, { timeout: idleTimeout });

  function teardown() {
    controller.abort();
    if (observer) {
      observer.disconnect();
      observer = undefined;
    }
    if (idleId !== undefined) {
      cancelIdle(idleId);
      idleId = undefined;
    }
  }

  function activate() {
    if (ready.value) return;
    ready.value = true;
    teardown();
  }

  onMounted(() => {
    const element = target?.value;

    if (typeof IntersectionObserver === 'undefined' || !element) {
      activate();
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) activate();
      },
      { rootMargin }
    );
    observer.observe(element);

    window.addEventListener('pointerdown', activate, {
      passive: true,
      signal: controller.signal,
    });
    idleId = scheduleIdle(activate);
  });

  onBeforeUnmount(teardown);

  return ready;
}
